import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import {
  API_POLICY,
  API_POLICY_REGIONS,
} from "../../config/Endpoints";
import { getApi, postApi, putApi } from "../../utils/api";
import { ThreeDots } from "react-loader-spinner";
import * as Yup from "yup";
import { FileText, Trash2 } from "lucide-react";
import { Toggle } from "../../v2/components/ui/toggle/toggle.tsx";
import { showError, showSuccess } from "../../v2/utils/toast.ts";

import { Modal } from "../../v2/components/ui/modal/modal.tsx";
import { TextField } from "../../v2/components/ui/text-field/text-field.tsx";
import { Select } from "../../v2/components/ui/select/select.tsx";
import { TextArea } from "../../v2/components/ui/text-area/text-area.tsx";
import { Button } from "../../v2/components/ui/button/button.tsx";
import {
  DatePicker,
  DatePickerFieldTrigger,
} from "../../v2/components/ui/date-picker/date-picker.tsx";

const FORM_ID = "policy-modal-form";

/** v2 DatePicker defaults maxDate to today when omitted; policies need future dates. */
const POLICY_VALID_FROM_MAX_DATE = new Date(2099, 11, 31);

function parsePolicyValidFrom(iso) {
  if (iso == null || String(iso).trim() === "") return null;
  const s = String(iso).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function toIsoDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getApiErrorMessage(error, fallback = "Something went wrong please try again!") {
  const msg = error?.response?.data?.message;
  if (typeof msg === "string" && msg.trim() !== "") return msg;
  if (typeof error?.message === "string" && error.message.trim() !== "")
    return error.message;
  return fallback;
}

/** PDF file required unless edit flow is keeping the existing uploaded document. */
function isPolicyDocumentMissing(selectedPolicy, isFileChange, policyDocument) {
  const isEdit = Boolean(selectedPolicy?.policyId);
  const keepingExisting =
    isEdit &&
    isFileChange &&
    selectedPolicy?.documentPath != null &&
    String(selectedPolicy.documentPath).trim() !== "";
  if (keepingExisting) return false;
  return !(policyDocument instanceof File);
}

const PolicyModal = ({
  open,
  onClose = () => { },
  selectedPolicy,
  setSelectedPolicy,
  fetchList,
}) => {
  const [formData, setFormData] = useState({
    policyTitle: "",
    region: "",
    policyDocument: null,
    policyValidFrom: "",
    description: "",
    activeStatus: true,

  });
  const [regionOptions, setRegionOptions] = useState([]);
  const [isFileChange, setIsFileChange] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const [errors, setErrors] = useState({});

  const handleInputChange = (event) => {
    const { name, type } = event.target;
    if (type === "file") {
      const file = event.target.files[0];
      // Define an array of accepted file types
      const acceptedFileTypes = ["application/pdf"];

      if (file) {
        if (acceptedFileTypes.includes(file.type)) {
          // File type is supported
          setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: file,
          }));
        } else {
          // File type is not supported, show an alert
          showError(
            "Unsupported file type. Please select a valid  PDF file.",
          );

          event.target.value = null; // Clear the file input
        }
      }
    } else if (type === "date") {
      const value = event.target.value;
      const year = value.split("-")[0];
      if (year.length <= 4) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else {
      const value = event.target.value;
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }

    setErrors((prevFormData) => ({
      ...prevFormData,
      [name]: "",
    }));
  };

  const handleReset = () => {
    setIsFileChange(false);
    setFormData({
      policyTitle: "",
      region: "",
      policyDocument: null,
      policyValidFrom: "",
      description: "",
      activeStatus: true,

    });
    if (!isFileChange) {
      const el = document.getElementById("file-input");
      if (el) el.value = "";
    }
    setErrors({});
    setSelectedPolicy({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await AddPolicySchema.validate(
        {
          policyTitle: formData.policyTitle,
          region: formData.region,
          description: formData.description,
        },
        {
          abortEarly: false,
        }
      );

      if (
        isPolicyDocumentMissing(
          selectedPolicy,
          isFileChange,
          formData.policyDocument,
        )
      ) {
        const msg = "Please select a PDF policy document.";
        setErrors((prev) => ({ ...prev, policyDocument: msg }));
        setIsLoading(false);
        setTimeout(() => {
          document.getElementById("file-input")?.reportValidity();
        }, 0);
        return;
      }

      if (selectedPolicy?.policyId) {
        const formdata = new FormData();
        formdata.append("title", formData.policyTitle);
        formdata.append("shiftType", formData.region);
        formdata.append("policyId", selectedPolicy?.policyId);
        if (formData.policyDocument instanceof File) {
          formdata.append("policyFile", formData.policyDocument);
        }
        formdata.append("validFrom", formData.policyValidFrom);
        formdata.append("description", formData.description);
        formdata.append("activeStatus", formData.activeStatus);
        await putApi(API_POLICY, formdata, accessToken)
          .then((res) => {
            setIsLoading(false);
            showSuccess(res.data.message);

            handleReset();
            fetchList();
            onClose();
          })
          .catch((e) => {
            console.log(e);
            setIsLoading(false);
            showError(getApiErrorMessage(e));
          });
      } else {
        const formdata = new FormData();
        formdata.append("title", formData.policyTitle);
        formdata.append("shiftType", formData.region);
        formdata.append("policyFile", formData.policyDocument);
        formdata.append("validFrom", formData.policyValidFrom);
        formdata.append("description", formData.description);
        formdata.append("activeStatus", formData.activeStatus);
        await postApi(API_POLICY, formdata, accessToken)
          .then((res) => {
            setIsLoading(false);
            showSuccess(res.data.message);

            handleReset();
            fetchList();
            onClose();
          })
          .catch((e) => {
            console.log(e);
            setIsLoading(false);
            showError(getApiErrorMessage(e));
          });
      }
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setIsLoading(false);

      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (Object.keys(selectedPolicy).length > 0) {
      setFormData({
        policyTitle: selectedPolicy.title ?? "",
        region:
          selectedPolicy.shiftType ||
          selectedPolicy.region ||
          selectedPolicy.policyRegion ||
          selectedPolicy.regionName ||
          "",
        policyDocument: null,
        policyValidFrom: selectedPolicy.validFrom
          ? String(selectedPolicy.validFrom).slice(0, 10)
          : "",
        description: selectedPolicy.description ?? "",
        activeStatus:
          selectedPolicy.activeStatus !== undefined
            ? selectedPolicy.activeStatus
            : true,
      });
      setIsFileChange(selectedPolicy?.policyId ? true : false);
    }
  }, [selectedPolicy]);

  useEffect(() => {
    getApi(API_POLICY_REGIONS, accessToken)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res?.data?.data ?? res?.data;
          let options = [];

          if (Array.isArray(responseData)) {
            options = responseData
              .map((item) =>
                typeof item === "string"
                  ? item
                  : item?.region || item?.name || item?.value || ""
              )
              .filter(Boolean);
          } else if (Array.isArray(responseData?.regions)) {
            options = responseData.regions
              .map((item) =>
                typeof item === "string"
                  ? item
                  : item?.region || item?.name || item?.value || ""
              )
              .filter(Boolean);
          }

          setRegionOptions([...new Set(options)]);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [accessToken]);

  const getLastPDFFileName = (filePath) => {
    // Split the file path by "/"
    const parts = filePath.split("/");

    // Initialize a variable to store the last file name
    let lastFileName = null;

    // Iterate through the parts in reverse order
    for (let i = parts.length - 1; i >= 0; i--) {
      const fileName = parts[i];

      // Check if the current part ends with ".pdf"
      if (fileName.endsWith(".pdf")) {
        lastFileName = fileName;
        break; // Stop iterating once a filename with ".pdf" extension is found
      }
    }

    return lastFileName;
  };

  const regionSelectOptions = useMemo(() => {
    const opts = regionOptions.map((r) => ({ label: r, value: r }));
    if (formData.region && !regionOptions.includes(formData.region)) {
      return [{ label: formData.region, value: formData.region }, ...opts];
    }
    return opts;
  }, [regionOptions, formData.region]);

  const modalTitle =
    Object.keys(selectedPolicy).length > 0 ? "Update Policy" : "Add Policy";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      size="lg"
      closeOnBackdropClick={false}
      closeOnEscape={false}
      footer={
        <div className="d-flex flex-wrap gap-2 justify-content-end w-100">
          <Button
            type="submit"
            form={FORM_ID}
            variant="accent"
            disabled={isLoading}
          >
            {isLoading ? (
              <ThreeDots
                height="20"
                width="53"
                radius="9"
                color="white"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
            ) : (
              "Submit"
            )}
          </Button>
          <Button
            type="button"
            variant="light-grey"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div className="row mt-1 justify-content-center">
          <div className="col-md-8 d-flex flex-column gap-3">
            <TextField
              id="policy-title"
              label="Title"
              labelLayout="inline"
              required
              type="text"
              name="policyTitle"
              value={formData.policyTitle}
              onChange={handleInputChange}
              placeholder="Enter Policy Title"
              maxLength={150}
              error={errors.policyTitle}
              onKeyPress={(e) => {
                if (e.charCode === 32 && e.target.selectionStart === 0) {
                  e.preventDefault(); // Prevent adding space at the beginning
                }
              }}
            />

            <Select
              id="policy-region"
              label="Region"
              labelLayout="inline"
              required
              placeholder="Select Region"
              value={formData.region}
              options={regionSelectOptions}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, region: v }));
                setErrors((prev) => ({ ...prev, region: "" }));
              }}
              error={errors.region}
            />

            {selectedPolicy?.policyId ? (
              <>
                {isFileChange && selectedPolicy?.documentPath !== "" ? (
                  <div className="d-flex flex-row flex-wrap align-items-start gap-3 w-100">
                    <span className="sub-heading-md text-accent pt-1">
                      Policy Document
                      <span className="text-danger text-nowrap" aria-hidden>
                        {"\u00A0*"}
                      </span>
                    </span>
                    <div className="flex-grow-1 min-w-0 rounded-3 border d-flex align-items-center justify-content-between gap-2 px-3 py-2 bg-light">
                      <span className="d-inline-flex align-items-center gap-2 min-w-0 flex-grow-1">
                        <FileText
                          size={18}
                          strokeWidth={2}
                          className="text-muted flex-shrink-0"
                          aria-hidden
                        />
                        <span
                          className="text-break body-sm"
                          title={getLastPDFFileName(selectedPolicy?.documentPath) ?? undefined}
                        >
                          {getLastPDFFileName(selectedPolicy?.documentPath)}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="danger-ghost"
                        size="sm"
                        className="flex-shrink-0"
                        aria-label="Remove selected file and upload a new document"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsFileChange(false);
                        }}
                      >
                        <Trash2 size={16} strokeWidth={2} aria-hidden />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <TextField
                    id="file-input"
                    type="file"
                    name="policyDocument"
                    label="Policy Document"
                    labelLayout="inline"
                    required
                    error={errors.policyDocument}
                    onChange={handleInputChange}
                    accept=".pdf,application/pdf"
                  />
                )}
              </>
            ) : (
              <TextField
                id="file-input"
                type="file"
                name="policyDocument"
                label="Policy Document"
                labelLayout="inline"
                required
                error={errors.policyDocument}
                onChange={handleInputChange}
                accept=".pdf,application/pdf"
              />
            )}

            <DatePicker
              id="policy-valid-from"
              label="Policy Valid From"
              labelLayout="inline"
              required
              selected={parsePolicyValidFrom(formData.policyValidFrom)}
              onChange={(date) => {
                setFormData((prev) => ({
                  ...prev,
                  policyValidFrom: toIsoDateString(date),
                }));
                setErrors((prev) => ({ ...prev, policyValidFrom: "" }));
              }}
              dateFormat="MMM d, yyyy"
              placeholderText="Select date"
              maxDate={POLICY_VALID_FROM_MAX_DATE}
              customInput={
                <DatePickerFieldTrigger placeholderText="Select date" />
              }
              error={errors.policyValidFrom}
            />

            <TextArea
              id="policy-description"
              label="Brief Description About Policy"
              labelLayout="inline"
              required
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              maxLength={160}
              error={errors.description}
              onKeyPress={(e) => {
                // Prevent adding space at the beginning
                if (e.charCode === 32 && e.target.selectionStart === 0) {
                  e.preventDefault();
                }

                // Regular expression to allow only letters, numbers, and spaces
                const allowedCharsRegex = /^[a-zA-Z0-9 ]*$/;

                // Check if the character is allowed
                const char = String.fromCharCode(e.charCode);
                if (!allowedCharsRegex.test(char)) {
                  e.preventDefault(); // Prevent non-alphanumeric character input
                }
              }}
            />
            <Toggle
              label="Policy Status"
              activeText="Active"
              inactiveText="Inactive"
              checked={formData.activeStatus}
              onCheckedChange={(checked) => {
                setFormData((prev) => ({
                  ...prev,
                  activeStatus: checked,
                }));
              }}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

const AddPolicySchema = Yup.object().shape({
  policyTitle: Yup.string().trim().required("Title is required."),
  region: Yup.string().trim().required("Region is required."),
  description: Yup.string().trim().required("Description is required."),
});

export default PolicyModal;
